import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Heading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import SalesInvoicesDraftsPanel from "./sales-invoices-drafts-panel";
import SalesInvoicesRegister from "./sales-invoices-register";

type OutgoingTab = "draft" | "confirmed" | "register";

/** Company-level outgoing (sales) invoices — drafts to confirm, completed ones, and the register of issued ones. */
export default function OutgoingInvoices() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = (searchParams.get("tab") || "draft") as OutgoingTab;
  const status: OutgoingTab =
    tabParam === "confirmed" || tabParam === "register" ? tabParam : "draft";

  const setTab = (next: OutgoingTab) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params);
  };

  const tabs: { id: OutgoingTab; label: string }[] = [
    {
      id: "draft",
      label: t("outgoing_invoices_tab_draft", { defaultValue: "Ready" }),
    },
    {
      id: "confirmed",
      label: t("outgoing_invoices_tab_complete", {
        defaultValue: "Confirmed",
      }),
    },
    {
      id: "register",
      label: t("outgoing_invoices_tab_register", { defaultValue: "Register" }),
    },
  ];

  return (
    <div className="space-y-5 px-4 py-2 md:px-1">
      <div>
        <Heading className="!font-bold">
          {t("outgoing_invoices", { defaultValue: "Outgoing invoices" })}
        </Heading>
        <Text className="mt-1 text-zinc-500">
          {t("outgoing_invoices_subtitle", {
            defaultValue:
              "Sales invoices: confirm drafts, or browse and send confirmed invoices.",
          })}
        </Text>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 dark:border-zinc-700">
        {tabs.map((tab) => {
          const active = status === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              aria-current={active ? "true" : undefined}
              onClick={() => setTab(tab.id)}
              className={`-mb-px border-b-2 px-3 py-2 max-md:min-h-11 text-sm font-medium transition ${
                active
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {status === "register" ? (
        <SalesInvoicesRegister />
      ) : (
        <SalesInvoicesDraftsPanel status={status} />
      )}
    </div>
  );
}
